import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import updateShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/UpdateShipTo";
import {
    getCurrentShipTosDataView,
    getCurrentShipToState,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { BaseAddressModel, CustomerValidationDto, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import CustomerAddressForm from "@insite/content-library/Components/CustomerAddressForm";
import ShipToSelector, { ShipToSelectorStyles } from "@insite/content-library/Components/ShipToSelector";
import { AddressesPageContext } from "@insite/content-library/Pages/AddressesPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Info from "@insite/mobius/Icons/Info";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioProps } from "@insite/mobius/Radio/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup/RadioGroup";
import TextField, { TextFieldProps } from "@insite/mobius/TextField/TextField";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip/Tooltip";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden/VisuallyHidden";
import WORAddressInfoDisplay from "@tKgpco/Components/WOR/WORAddressInfoDisplay";
import CustomerAddressFormFields, {
    CustomerAddressFormFieldStyles,
} from "@tKgpco/Components/WOR/WORCustomerAddressFormFields";
import { getWORData, getWORDeliveryInfoData, setDeliveryContactInfoInLocalStorage } from "@tKgpco/Services/WORService";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import { useWORCheckoutShippingFormContext } from "@tKgpco/Widgets/WOR/WORCheckoutShippingPage";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    currentShipTo: getCurrentShipToState(state).value,
    currentShipTosDataView: getCurrentShipTosDataView(state),
    currentBillTo: getCurrentBillToState(state).value,
    countries: getCurrentCountries(state),
    shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
    customerSettings: getSettingsCollection(state).customerSettings,
});

const mapDispatchToProps = {
    updateShipTo,
    setCurrentShipTo,
    loadCurrentShipTos,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CurrentShippingAddressStyles {
    gridContainer?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyProps;
    editLinkGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    addressFormModal?: ModalPresentationProps;
    addressInfoDisplayGridItem?: GridItemProps;
    useBillingAddressButtonGridItem?: GridItemProps;
    useBillingAddress?: UseBillingAddressStyles;
    selectSavedAddressLink?: LinkPresentationProps;
    headerGridItem?: GridItemProps;
    shipTooltip?: TooltipPresentationProps;
    title?: InjectableCss;
    addressBookModal?: ModalPresentationProps;
    shipToSelector?: ShipToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    oneTimeAddressGridItem?: GridItemProps;
    oneTimeAddressCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    oneTimeAddressCheckbox?: CheckboxPresentationProps;
    formFields?: CustomerAddressFormFieldStyles;
    asterisk?: TypographyProps;
    requiredMsgWrapper?: InjectableCss;
    deliveryinfoContainer?: GridContainerProps;
    worInfoContainer?: InjectableCss;
    worInfoNote?: TypographyPresentationProps;
}
interface UseBillingAddressStyles {
    button?: ButtonPresentationProps;
}

export const currentShippingAddressStyles: CurrentShippingAddressStyles = {
    gridContainer: {
        gap: 5,
        css: css`
            padding: 10px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
            padding: 15px 0;
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
            font-size: 14px !important;
        `,
    },
    editLinkGridItem: {
        width: 2,
        css: css`
            font-size: 1rem;
            justify-content: flex-end;
        `,
    },
    addressFormModal: { sizeVariant: "medium" },
    addressInfoDisplayGridItem: {
        width: 12,
        css: css`
            margin-bottom: 10px !important;
        `,
    },
    useBillingAddressButtonGridItem: { width: 12 },
    useBillingAddress: {
        button: {
            // color: "secondary",
            typographyProps: {
                size: TYPOGRAPHY.size.smaller,
                weight: TYPOGRAPHY.weight.bold,
            },
            css: css`
                margin-right: 10px;
                text-transform: uppercase;
                border-radius: 22px;
                border: 1px solid ${COLORS.secondaryBtn};
                min-width: 120px;
                background: ${COLORS.white};
                color: ${COLORS.secondaryBtn};
                & :hover {
                    background: ${COLORS.secondaryBg};
                    border: 1px solid ${COLORS.secondaryBtn};
                }
                &:disabled:hover {
                    background: ${COLORS.disabled.base};
                    border: 1px solid ${COLORS.disabled.base};
                }
            `,
        },
    },
    selectSavedAddressLink: {
        css: css`
            text-transform: capitalize;
            border-radius: 22px;
            border: 1px solid ${COLORS.secondaryBtn};
            padding: 0px 21px;
            min-height: 40px;
            width: 100%;
            justify-content: center;
            font-size: 14px;
            color: ${COLORS.secondaryBtn};
            font-weight: 600;
            &:hover {
                background: ${COLORS.secondaryBg};
                border: 1px solid ${COLORS.secondaryBtn};
                span {
                    color: ${COLORS.secondaryBtn} !important;
                }
            }
        `,
    },
    headerGridItem: {
        width: 12,
        css: css`
            background: #f1f6fb;
            padding: 14px;
            border-bottom: 2px solid #1e75ba;
            border-radius: 8px 8px 0 0;
        `,
    },
    shipTooltip: {
        iconProps: {
            src: Info,
            css: css`
                svg {
                    color: ${COLORS.primary.base};
                    width: 18px;
                    height: 18px;
                }
            `,
        },
        cssOverrides: {
            tooltipContainer: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                left: auto !important;
                                right: -5px !important;
                                &:after {
                                    content: "";
                                    left: auto !important;
                                    right: 11px !important;
                                }
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
        },
    },
    title: {
        css: css`
            font-size: 14px;
            display: flex;
            justify-content: space-between;
            width: 100%;
            align-items: center;
        `,
    },
    asterisk: {
        css: css`
            color: #ff0000;
            margin-right: 4px;
        `,
    },
    requiredMsgWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            padding: 20px 0;
            width: 100%;
            font-size: 13px;
        `,
    },
    formFields: {
        container: {
            css: css`
                & input,
                & select {
                    font-size: 13px !important;
                }
                & label {
                    font-size: 13px;
                    & + div {
                        margin-top: 0 !important;
                    }
                }
            `,
        },
    },
    deliveryinfoContainer: {
        css: css`
            margin-top: 15px;
        `,
    },
    worInfoContainer: {
        css: css`
            width: 100%;
            margin-bottom: 20px;
            p {
                font-size: 13px;
                text-transform: uppercase;
                font-weight: 500;
            }
        `,
    },
    worInfoNote: {
        size: 13,
        weight: 500,
        css: css`
            display: block;
            margin-top: 20px;
        `,
    },
};

const styles = currentShippingAddressStyles;

const validation: CustomerValidationDto = {
    firstName: {
        isRequired: true,
        isDisabled: false,
        maxLength: 30,
    },
    lastName: {
        isRequired: true,
        isDisabled: false,
        maxLength: 30,
    },
    companyName: {
        isRequired: true,
        isDisabled: false,
        maxLength: 40,
    },
    attention: {
        isRequired: false,
        isDisabled: false,
        maxLength: 50,
    },
    address1: {
        isRequired: true,
        isDisabled: false,
        maxLength: 40,
    },
    address2: {
        isRequired: false,
        isDisabled: false,
        maxLength: 40,
    },
    address3: {
        isRequired: false,
        isDisabled: false,
        maxLength: 40,
    },
    address4: {
        isRequired: false,
        isDisabled: false,
        maxLength: 40,
    },
    country: {
        isRequired: true,
        isDisabled: false,
        maxLength: null,
    },
    state: {
        isRequired: true,
        isDisabled: false,
        maxLength: null,
    },
    city: {
        isRequired: true,
        isDisabled: false,
        maxLength: 30,
    },
    postalCode: {
        isRequired: true,
        isDisabled: false,
        maxLength: 10,
    },
    phone: {
        isRequired: true,
        isDisabled: false,
        maxLength: 10,
    },
    email: {
        isRequired: false,
        isDisabled: false,
        maxLength: 50,
    },
    fax: {
        isRequired: false,
        isDisabled: false,
        maxLength: 20,
    },
};

const CurrentShippingAddress: React.FunctionComponent<Props> = (props: Props) => {
    const { currentShipTo: pCurrentShipto, currentBillTo, currentShipTosDataView, loadCurrentShipTos } = props;
    const [isCustomerSelectorModalOpen, setIsCustomerSelectorModalOpen] = useState(false);
    const [addressBeingEdited, setAddressBeingEdited] = useState<ShipToModel | undefined>(undefined);
    const [currentShipTo, setCurrentShipTo] = useState<ShipToModel | undefined>(pCurrentShipto);
    // const [useOneTimeAddress, setUseOneTimeAddress] = useState<boolean>(false);
    const [newAddress, setNewAddress] = useState<ShipToModel | undefined>();
    const [shippingAddressFormState, setShippingAddressFormState] = useState<ShipToModel | undefined>();
    // const [formErrors, setFormErrors] = useState<any>({});
    // const [formState, setFormState] = useState<any>({});
    const { useOneTimeAddress, useOrderShipto, formState, formErrors, dispatch } = useWORCheckoutShippingFormContext();
    const worData = getWORData();
    const [worDeliveryInfo, setWordDeliveryInfo] = useState<any>(getWORDeliveryInfoData().updated);
    const [allowSaveAddresses, setAllowSaveAddresses] = useState<boolean>(false);

    // Disallowed special characters
    const forbiddenChars = /[^a-zA-Z0-9\s]/;
    // Check for overly long spacing (more than one consecutive space)
    const excessiveSpacing = / {2,}/;

    useEffect(() => {
        if (worData?.useOneTimeAddress) {
            dispatch({
                useOneTimeAddress: true,
                useOrderShipto: false,
                formState: {},
                formErrors: {},
            });
        }
    }, []);

    useEffect(() => {
        if (currentBillTo?.properties?.allowSaveAddresses) {
            setAllowSaveAddresses(currentBillTo?.properties?.allowSaveAddresses === "true");
        }
    }, [currentBillTo?.properties]);

    useEffect(() => {
        // we reset all dataviews when adding a new address, so we have to load these if they go away
        if (!currentShipTosDataView.value && !currentShipTosDataView.isLoading) {
            loadCurrentShipTos();
        }
    }, [currentShipTosDataView]);

    useEffect(() => {
        if (useOneTimeAddress) {
            const worData = getWORData().formState || {};

            const customFormState = {
                // ...formState,
                ...worData,
                firstName: worDeliveryInfo?.firstName,
                lastName: worDeliveryInfo?.lastName,
                phone: worDeliveryInfo?.phone,
                email: worDeliveryInfo?.email,
                address3: worDeliveryInfo?.address3,
                address4: worDeliveryInfo?.address4,
            };

            const filteredValidation: Record<string, string> = Object.keys(validation).reduce(
                (acc, key) => {
                    const validationField = validation[key as keyof CustomerValidationDto]; // Type assertion here
                    if (validationField?.isRequired && !customFormState[key]) {
                        const value = "";
                        acc[key] = value; // Set the value to an empty string
                    }
                    return acc;
                },
                {} as Record<string, string>,
            );
            dispatch({
                formState: { ...customFormState, ...(worData?.state?.name && { state: worData.state }) },
                formErrors: { ...filteredValidation },
            });

            Object.entries(customFormState).forEach(([key, value]) => {
                const strValue = value as string; // Type assertion

                if (
                    (key === "firstName" || key === "lastName") &&
                    value &&
                    (forbiddenChars.test(strValue) || excessiveSpacing.test(strValue))
                ) {
                    const errMsg = forbiddenChars.test(strValue)
                        ? `Special Characters Not Allowed`
                        : `Excessive Spacing Not Allowed`;

                    dispatch({
                        formState: { ...formState, [key]: strValue },
                        formErrors: { ...formErrors, [key]: errMsg },
                    });
                }
                if (key === "email" && /[^a-zA-Z0-9\s@.]/.test(strValue)) {
                    const errMSg = "Email addresses can only contain letters, numbers, @, and periods";
                    dispatch({
                        formState: { ...formState, key: strValue },
                        formErrors: { ...formErrors, key: errMSg },
                    });
                }
            });
        }
    }, [useOneTimeAddress]);

    useEffect(() => {
        if (worDeliveryInfo) {
            const worDeliveryInfoFromStorage = getWORDeliveryInfoData();
            setDeliveryContactInfoInLocalStorage({
                ...worDeliveryInfoFromStorage,
                updated: {
                    ...worDeliveryInfo,
                },
            });
            dispatch({
                worDeliveryInfo: {
                    ...worDeliveryInfoFromStorage,
                    updated: {
                        ...worDeliveryInfo,
                    },
                },
            });
            if (!useOneTimeAddress) {
                Object.entries(worDeliveryInfo).forEach(([key, value]) => {
                    const strValue = value as string; // Type assertion

                    if (
                        (key === "firstName" || key === "lastName") &&
                        value &&
                        (forbiddenChars.test(strValue) || excessiveSpacing.test(strValue))
                    ) {
                        const errMsg = forbiddenChars.test(strValue)
                            ? `Special Characters Not Allowed`
                            : `Excessive Spacing Not Allowed`;

                        dispatch({
                            formState: { ...formState, [key]: strValue },
                            formErrors: { ...formErrors, [key]: errMsg },
                        });
                    }
                    if (key === "email" && /[^a-zA-Z0-9\s@.]/.test(strValue)) {
                        const errMSg = "Email addresses can only contain letters, numbers, @, and periods";
                        dispatch({
                            formState: { ...formState, key: strValue },
                            formErrors: { ...formErrors, key: errMSg },
                        });
                    }
                });
            }
        }
    }, [worDeliveryInfo, useOneTimeAddress]);

    useEffect(() => {
        if (!currentShipTo && !useOrderShipto) {
            setCurrentShipTo(pCurrentShipto);
            dispatch({ shippingAddress: pCurrentShipto });
        } else if (useOrderShipto) {
            if (Object.keys(worData).length > 0) {
                const {
                    orderShipto: {
                        stAddress1,
                        stAddress2,
                        stAddress3,
                        stAddress4,
                        stCity,
                        stCompanyName,
                        stCountry,
                        stPostalCode,
                        stState,
                    },
                } = worData;
                const shipTo = {
                    ...pCurrentShipto,
                    address1: stAddress1,
                    address2: stAddress2,
                    address3: stAddress3,
                    address4: stAddress4,
                    city: stCity,
                    companyName: stCompanyName,
                    country: stCountry,
                    postalCode: stPostalCode,
                    state: stState,
                } as ShipToModel;
                setCurrentShipTo(shipTo);
                dispatch({ shippingAddress: shipTo });
            }
        }
    }, [pCurrentShipto, useOrderShipto]);

    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    if (!pCurrentShipto || !currentShipTo || !currentBillTo || !currentShipTosDataView.value) {
        return null;
    }

    const billToAsShipTo = currentShipTosDataView.value.find(o => o.id === currentBillTo.id);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const editClickHandler = () => {
        setModalIsOpen(true);
    };
    const useBillingAddressHandler = () => {
        if (!billToAsShipTo) {
            return;
        }
        props.setCurrentShipTo({
            shipToId: billToAsShipTo.id,
        });
    };
    const formCancelHandler = (e: any) => {
        e.preventDefault();
        modalCloseHandler();
    };
    const formSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        if (!currentBillTo || !currentShipTo) {
            return;
        }

        // props.updateShipTo({
        //     billToId: currentBillTo.id,
        //     shipTo: {
        //         ...currentShipTo,
        //         ...address,
        //     },
        // });
        modalCloseHandler();
    };

    const selectAddressClickHandler = () => setIsCustomerSelectorModalOpen(true);

    const customerSelectorModalCloseHandler = () => setIsCustomerSelectorModalOpen(false);

    const selectCustomerHandler = (shipTo: ShipToModel) => {
        customerSelectorModalCloseHandler();
        setCurrentShipTo(shipTo);
        dispatch({ shippingAddress: shipTo, useOrderShipto: false, useOneTimeAddress: false });
        // onChange(shipTo);
    };

    const editCustomerHandler = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        customer: BaseAddressModel,
    ) => {
        event.preventDefault();
        setAddressBeingEdited(customer as ShipToModel);
    };

    const createNewAddressHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        // setIsCreateNewAddressModalOpen(true);
    };

    const { countries, shipToAddressFields, customerSettings } = props;

    const currentBillToId = currentBillTo.id;

    const oneTimeAddressChangeHandler: CheckboxProps["onChange"] = (_, value) => dispatch({ useOneTimeAddress: value });

    const addressTypeChangeHandler: RadioGroupProps["onChangeHandler"] = event => {
        if (event.target.value === "useOneTimeShipto") {
            dispatch({
                useOneTimeAddress: true,
                useOrderShipto: false,
                formState: {},
                formErrors: {},
            });
        } else if (event.target.value === "useOrderShipto") {
            dispatch({ useOneTimeAddress: false, useOrderShipto: true });
        } else if (event.target.value === "useSavedAddress") {
            setIsCustomerSelectorModalOpen(true);
        }
        setWordDeliveryInfo({ ...getWORDeliveryInfoData().default });
        // dispatch({ useOneTimeAddress: event.target.value === "useOneTimeShipto" });
    };

    const handleInputChange: TextFieldProps["onChange"] = event => {
        const name = event.target.name as keyof CustomerValidationDto;
        const value =
            name === "phone" && event.target.value.length === 10
                ? formatPhoneNumber(event.target.value)
                : event.target.value.split("-").join("");

        if (validation[name] && validation[name]?.isRequired && !value.trim()) {
            const msgName =
                name === "firstName"
                    ? "First Name"
                    : name === "lastName"
                      ? "Last Name"
                      : name === "postalCode"
                        ? "Postal Code"
                        : name === "companyName"
                          ? "Company Name"
                          : name.charAt(0).toUpperCase() + name.slice(1);
            const errMSg = `${msgName} is required`;

            dispatch({
                formState: { ...formState, [event.target.name]: event.target.value },
                formErrors: { ...formErrors, [name]: errMSg },
            });
        } else if (name === "postalCode" && /[^a-zA-Z0-9\s-]/.test(value)) {
            const errMSg = "Postal codes can only contain letters, numbers, and hyphens";
            dispatch({
                formState: { ...formState, [event.target.name]: event.target.value },
                formErrors: { ...formErrors, [name]: errMSg },
            });
        } else if (name === "email" && /[^a-zA-Z0-9\s@.]/.test(value)) {
            const errMSg = "Email addresses can only contain letters, numbers, @, and periods";
            dispatch({
                formState: { ...formState, [event.target.name]: event.target.value },
                formErrors: { ...formErrors, [name]: errMSg },
            });
        } else if (
            name !== "phone" &&
            name !== "email" &&
            name !== "postalCode" &&
            name !== "address4" &&
            name !== "address3" &&
            name !== "companyName" &&
            name !== "fax" &&
            value &&
            (forbiddenChars.test(value) || excessiveSpacing.test(value))
        ) {
            const errMSg = forbiddenChars.test(value)
                ? `Special Characters Not Allowed`
                : `Excessive Spacing Not Allowed`;

            dispatch({
                formState: { ...formState, [event.target.name]: event.target.value },
                formErrors: { ...formErrors, [name]: errMSg },
            });
        } else if (name === "phone" && value.split("-").join("").length !== 10) {
            dispatch({
                formState: { ...formState, [event.target.name]: event.target.value.split("-").join("") },
                formErrors: { ...formErrors, [name]: "Enter Valid Phone Numner" },
            });
        } else {
            const errors = { ...formErrors };
            delete errors[name];
            dispatch({
                formState: {
                    ...formState,
                    [event.target.name]:
                        name === "phone" && event.target.value.length === 10
                            ? formatPhoneNumber(event.target.value)
                            : event.target.value.split("-").join(""),
                },
                formErrors: errors,
            });
        }

        if (Object.keys(worDeliveryInfo).find(item => item === name)) {
            const valueInfo =
                name === "phone" && event.target.value.length === 10
                    ? formatPhoneNumber(event.target.value)
                    : event.target.value.split("-").join("");
            setWordDeliveryInfo((prev: any) => {
                return {
                    ...prev,
                    [name]: valueInfo,
                };
            });
        }
    };

    const handleCountryChange = (country: BaseAddressModel["country"], _?: React.ChangeEvent<HTMLSelectElement>) => {
        if (!country) {
            dispatch({
                formState: { ...formState, country, state: undefined },
                formErrors: { ...formErrors, country: `Country is required`, state: "" },
            });
        } else {
            const errors = { ...formErrors };
            delete errors.country;
            delete errors.state;
            dispatch({
                formState: { ...formState, country, state: undefined },
                formErrors: { ...errors, state: "" },
            });
        }
    };

    const handleStateChange = (_: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => {
        if (state && formState.country && formState.country.states && formState.country.states.length > 0) {
            const errors = { ...formErrors };
            delete errors.state;

            dispatch({ formState: { ...formState, state }, formErrors: errors });
        } else {
            dispatch({ formState: { ...formState, state }, formErrors: { ...formErrors, state: `State is required` } });
        }
    };

    const formatPhoneNumber = (value: string) => {
        if (!value) {
            return "";
        }

        // Remove all non-numeric characters
        const cleaned = `${value}`.replace(/\D/g, "");

        // Format the number as XXX-XXX-XXXX
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `${match[1]}-${match[2]}-${match[3]}`;
        }

        return value; // Return the original value if it doesn't match the pattern
    };

    const setDeliveryInfo = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
        const value =
            type === "phone" && e.target.value.length === 10
                ? formatPhoneNumber(e.target.value)
                : e.target.value.split("-").join("");

        handleInputChange(e);
        setWordDeliveryInfo((prev: any) => {
            return {
                ...prev,
                [type]: value,
            };
        });
    };

    return (
        <>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.headerGridItem}>
                    <StyledWrapper {...styles.title}>
                        <Typography as="h3" {...styles.headingText}>
                            {translate("Shipping Address")}
                        </Typography>
                        <Tooltip
                            {...styles.shipTooltip}
                            text="Shipments outside of the United States must be handled by your KGP Logistics representative. Please note: Special shipping requirements may apply to your order"
                        />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.headingGridItem}>
                    <Link
                        {...styles.selectSavedAddressLink}
                        type="button"
                        onClick={selectAddressClickHandler}
                        data-test-selector="checkoutShipping_selectSavedAddress"
                    >
                        {translate("Select Saved Address")}
                        <VisuallyHidden>{translate("For Shipping Address")}</VisuallyHidden>
                    </Link>
                </GridItem>
                {customerSettings.allowShipToAddressEdit && (
                    <GridItem {...styles.editLinkGridItem}>
                        {countries && (
                            <Link
                                {...styles.editLink}
                                onClick={editClickHandler}
                                data-test-selector="shipTo_Edit_Address"
                            >
                                {translate("Edit")}
                            </Link>
                        )}
                        {countries && shipToAddressFields && (
                            <Modal
                                headline={translate("Edit Shipping Information")}
                                {...styles.addressFormModal}
                                isOpen={modalIsOpen}
                                handleClose={modalCloseHandler}
                            >
                                <CustomerAddressForm
                                    address={currentShipTo}
                                    countries={countries}
                                    addressFieldDisplayCollection={shipToAddressFields}
                                    onCancel={formCancelHandler}
                                    onSubmit={formSubmitHandler}
                                />
                            </Modal>
                        )}
                    </GridItem>
                )}
                <GridItem {...styles.oneTimeAddressGridItem} width={12}>
                    {/* <CheckboxGroup {...styles.oneTimeAddressCheckboxGroup}>
                        <Checkbox
                            {...styles.oneTimeAddressCheckbox}
                            onChange={oneTimeAddressChangeHandler}
                            checked={useOneTimeAddress}
                        >
                            {translate("Ship to One-Time Address")}
                        </Checkbox>
                    </CheckboxGroup> */}
                    <RadioGroup
                        value={
                            useOrderShipto
                                ? "useOrderShipto"
                                : useOneTimeAddress
                                  ? "useOneTimeShipto"
                                  : "useSavedAddress"
                        }
                        onChangeHandler={addressTypeChangeHandler}
                    >
                        <Radio value="useOrderShipto" data-test-selector="checkoutShipping_useOneTimeAddress">
                            {translate("Use Order Shipping Address")}
                        </Radio>
                        {allowSaveAddresses && (
                            <Radio value="useSavedAddress" data-test-selector="checkoutShipping_useSavedAddress">
                                {translate("Use Saved Address")}
                            </Radio>
                        )}
                        <Radio value="useOneTimeShipto">{translate("Ship to One-Time Address")}</Radio>
                    </RadioGroup>
                </GridItem>

                {useOneTimeAddress ? (
                    <GridItem width={12}>
                        <StyledWrapper {...styles.worInfoContainer}>
                            <Typography as="p">{`${worDeliveryInfo?.firstName} ${worDeliveryInfo?.lastName}`}</Typography>
                            <Typography as="p">{`${worDeliveryInfo?.phone}`}</Typography>
                        </StyledWrapper>
                    </GridItem>
                ) : null}

                <GridItem
                    {...styles.addressInfoDisplayGridItem}
                    data-test-selector="addressInfoDisplay_CurrentShippingAddress"
                >
                    {countries && shipToAddressFields && useOneTimeAddress ? (
                        <StyledWrapper
                            css={css`
                                display: flex;
                                flex-direction: column;
                            `}
                        >
                            <CustomerAddressFormFields
                                firstName={formState.firstName || worDeliveryInfo?.firstName}
                                lastName={formState.lastName || worDeliveryInfo?.lastName}
                                attention={formState.attention}
                                companyName={formState.companyName}
                                address1={formState.address1}
                                address2={formState.address2}
                                address3={formState.address3 || worDeliveryInfo?.stAddress3}
                                address4={formState.address4 || worDeliveryInfo?.stAddress4}
                                stAddress3Disable={false}
                                stAddress4Disable={false}
                                country={formState.country}
                                city={formState.city}
                                state={formState.state}
                                postalCode={formState.postalCode}
                                phone={formState.phone || worDeliveryInfo?.phone}
                                email={formState.email || worDeliveryInfo?.email}
                                fax={formState.fax}
                                onChangeFirstName={handleInputChange}
                                onChangeLastName={handleInputChange}
                                onChangeAttention={handleInputChange}
                                onChangeCompanyName={handleInputChange}
                                onChangeAddress1={handleInputChange}
                                onChangeAddress2={handleInputChange}
                                onChangeAddress3={handleInputChange}
                                onChangeAddress4={handleInputChange}
                                onChangeCountry={handleCountryChange}
                                onChangeState={handleStateChange}
                                onChangeCity={handleInputChange}
                                onChangePostalCode={handleInputChange}
                                onChangePhone={handleInputChange}
                                onChangeEmail={handleInputChange}
                                onChangeFax={handleInputChange}
                                firstNameError={formErrors.firstName}
                                lastNameError={formErrors.lastName}
                                attentionError={formErrors.attention}
                                companyNameError={formErrors.companyName}
                                address1Error={formErrors.address1}
                                address2Error={formErrors.address2}
                                address3Error={formErrors.address3}
                                address4Error={formErrors.address4}
                                countryError={formErrors.country}
                                stateError={formErrors.state}
                                cityError={formErrors.city}
                                postalCodeError={formErrors.postalCode}
                                phoneError={formErrors.phone}
                                emailError={formErrors.email}
                                faxError={formErrors.fax}
                                countries={countries}
                                validation={validation}
                                fieldDisplay={shipToAddressFields}
                                extendedStyles={styles.formFields}
                                defaultCountry="US"
                            />
                            <StyledWrapper {...styles.requiredMsgWrapper}>
                                <Typography {...styles.asterisk}>*</Typography>
                                {translate("Required")}
                            </StyledWrapper>
                        </StyledWrapper>
                    ) : (
                        <WORAddressInfoDisplay
                            firstName={currentShipTo.firstName}
                            lastName={currentShipTo.lastName}
                            companyName={currentShipTo.companyName}
                            attention={currentShipTo.attention}
                            address1={currentShipTo.address1}
                            address2={currentShipTo.address2}
                            address3={!useOneTimeAddress && !useOrderShipto ? "" : currentShipTo.address3}
                            address4={!useOneTimeAddress && !useOrderShipto ? "" : currentShipTo.address4}
                            city={currentShipTo.city}
                            state={currentShipTo.state ? currentShipTo.state.abbreviation : undefined}
                            postalCode={currentShipTo.postalCode}
                            country={currentShipTo.country ? currentShipTo.country.abbreviation : undefined}
                            phone={currentShipTo.phone}
                            fax={currentShipTo.fax}
                            email={currentShipTo.email}
                            shipAddress3={worData.orderShipto.stAddress3}
                            shipAddress4={worData.orderShipto.stAddress4}
                        />
                    )}
                </GridItem>
                {/* {billToAsShipTo && currentShipTo.id !== billToAsShipTo.id && (
                    <GridItem {...styles.useBillingAddressButtonGridItem}>
                        <UseBillingAddressButton onClick={useBillingAddressHandler} />
                    </GridItem>
                )} */}

                {!useOneTimeAddress ? (
                    <>
                        <GridItem width={12}>
                            <StyledWrapper {...styles.worInfoContainer}>
                                <Typography as="p">{`${worDeliveryInfo?.firstName || ""} ${
                                    worDeliveryInfo?.lastName || ""
                                }`}</Typography>
                                <Typography as="p">{`${worDeliveryInfo?.phone || ""}`}</Typography>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.headerGridItem}>
                            <StyledWrapper {...styles.title}>
                                <Typography as="h3" {...styles.headingText}>
                                    {translate("Delivery Contact")}
                                </Typography>
                            </StyledWrapper>
                        </GridItem>

                        <GridContainer {...styles.deliveryinfoContainer}>
                            <GridItem width={6}>
                                <TextField
                                    name="firstName"
                                    value={worDeliveryInfo?.firstName}
                                    required
                                    label={translate("First Name")}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setDeliveryInfo(e, "firstName")
                                    }
                                    error={formErrors.firstName}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    name="lastName"
                                    value={worDeliveryInfo?.lastName}
                                    required
                                    label={translate("Last Name")}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setDeliveryInfo(e, "lastName")
                                    }
                                    error={formErrors.lastName}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    name="phone"
                                    value={worDeliveryInfo?.phone}
                                    required
                                    label={translate("Phone")}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeliveryInfo(e, "phone")}
                                    error={formErrors.phone}
                                    maxLength={validation.phone?.maxLength || undefined}
                                />
                            </GridItem>
                            <GridItem
                                width={12}
                                css={css`
                                    flex-direction: column;
                                `}
                            >
                                <TextField
                                    name="email"
                                    value={worDeliveryInfo?.email}
                                    label={translate("Email")}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeliveryInfo(e, "email")}
                                    error={formErrors.email}
                                />
                                <StyledWrapper {...styles.requiredMsgWrapper}>
                                    <Typography {...styles.asterisk}>*</Typography>
                                    {translate("Required")}
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    </>
                ) : null}
                <GridItem width={12}>
                    <Typography {...styles.worInfoNote}>{siteMessage("WOR_DeliveryInfoNote")}</Typography>
                </GridItem>
            </GridContainer>
            <Modal
                {...styles.addressBookModal}
                headline={translate("Address Book")}
                isOpen={isCustomerSelectorModalOpen}
                handleClose={customerSelectorModalCloseHandler}
                data-test-selector="checkoutShipping_addressModal"
            >
                <ShipToSelector
                    currentShipTo={useOneTimeAddress && shippingAddressFormState ? undefined : currentShipTo}
                    currentBillToId={currentBillToId}
                    newAddress={newAddress}
                    onSelect={selectCustomerHandler}
                    onEdit={editCustomerHandler}
                    onCreateNewAddressClick={newAddress && createNewAddressHandler}
                    extendedStyles={styles.shipToSelector}
                />
                <StyledWrapper {...styles.addressBookModalButtonsWrapper}>
                    <Button
                        {...styles.addressBookModalCancelButton}
                        onClick={customerSelectorModalCloseHandler}
                        data-test-selector="addressModal_cancel"
                    >
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};

interface UseBillingAddressButtonProps {
    onClick: () => void;
}
const UseBillingAddressButton: React.FunctionComponent<UseBillingAddressButtonProps> = (
    props: UseBillingAddressButtonProps,
) => {
    const componentStyles: UseBillingAddressStyles = styles.useBillingAddress || {};

    return (
        <Button {...componentStyles.button} onClick={props.onClick}>
            {translate("Use Billing Address")}
        </Button>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CurrentShippingAddress),
    definition: {
        displayName: "WOR Shipping Addresses",
        group: "Addresses",
    },
};

export default widgetModule;
